import { ModuleDefinitionTemplates } from "../../../../model/module/types/module-definition/ModuleDefinitionTemplates";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { InputTypes } from "../../../../model/form/types/InputTypes.enum";
import { t } from "i18next";
import { ModuleDefinition } from "../../../../model/module/types/module-definition/ModuleDefinition";
import uniq from "lodash/uniq";
import { BaseModuleInstance } from "../../../../model/module/types/module-instance/BaseModuleInstance";

export const isDateModuleInput = (
  moduleDefinition: ModuleDefinition,
  fieldKey: string,
) => {
  return (
    moduleDefinition?.inputs
      ?.find((i) => i.name === fieldKey)
      ?.type?.toLowerCase() === InputTypes.DATE.toLowerCase()
  );
};

export const moduleTemplates: Record<
  ModuleDefinitionTemplates,
  {
    type: InputTypes;
    label: string;
    settings: { required: boolean; maxChar?: number };
    name: string;
    order: number;
    options?: string[];
    value?: string;
  }[]
> = {
  [ModuleDefinitionTemplates.DEFAULT]: [
    {
      type: InputTypes.TEXT,
      label: t(`common.${InputTypes.TEXT}`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "text-0",
      order: 0,
      value: "Lorem ipsum dolor sit amet, consectetur adipiscing elit.",
    },
  ],
  [ModuleDefinitionTemplates.CALL]: [
    {
      type: InputTypes.TEXT,
      label: t(`common.name`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "name",
      order: 0,
      value: "John Doe",
    },
    {
      type: InputTypes.PHONE,
      label: t(`common.phone`),
      settings: { required: true, maxChar: Lengths.PHONE },
      name: "phone",
      order: 1,
      value: "+1234567890",
    },
    {
      type: InputTypes.TEXTAREA,
      label: t(`common.note`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "note",
      order: 2,
      value: "Discussed project requirements and next steps.",
    },
    {
      type: InputTypes.DATE,
      label: t(`common.remind_date`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "reminder",
      order: 3,
      value: new Date().toLocaleDateString(),
    },
  ],
  [ModuleDefinitionTemplates.VISIT]: [
    {
      type: InputTypes.TEXT,
      label: t(`common.name`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "name",
      order: 0,
      value: "John Doe",
    },
    {
      type: InputTypes.EMAIL,
      label: t(`common.email`),
      settings: { required: true, maxChar: Lengths.EMAIL },
      name: "email",
      order: 1,
      value: "john.doe@example.com",
    },
    {
      type: InputTypes.PHONE,
      label: t(`common.phone`),
      settings: { required: false, maxChar: Lengths.PHONE },
      name: "phone",
      order: 2,
      value: "+1234567890",
    },
    {
      type: InputTypes.TEXTAREA,
      label: t(`common.note`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "note",
      order: 3,
      value: "Client visit to discuss project updates.",
    },
    {
      type: InputTypes.DATE,
      label: t(`common.remind_date`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "reminder",
      order: 4,
      value: new Date().toLocaleDateString(),
    },
  ],
  [ModuleDefinitionTemplates.MEETING]: [
    {
      type: InputTypes.TEXT,
      label: t(`common.name`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "name",
      order: 0,
      value: "Project Kickoff Meeting",
    },
    {
      type: InputTypes.EMAIL,
      label: t(`common.email`),
      settings: { required: true, maxChar: Lengths.EMAIL },
      name: "email",
      order: 1,
      value: "contact@example.com",
    },
    {
      type: InputTypes.PHONE,
      label: t(`common.phone`),
      settings: { required: false, maxChar: Lengths.PHONE },
      name: "phone",
      order: 2,
      value: "+1234567890",
    },
    {
      type: InputTypes.TEXTAREA,
      label: t(`common.note`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "note",
      order: 3,
      value: "Initial meeting to discuss project scope and deliverables.",
    },
    {
      type: InputTypes.DATE,
      label: t(`common.remind_date`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "reminder",
      order: 4,
      value: new Date().toLocaleDateString(),
    },
    {
      type: InputTypes.RADIO,
      label: t(`common.meetingType`),
      settings: {
        required: true,
      },
      options: [t("common.online"), t("common.inperson")],
      name: "meetingType",
      order: 5,
      value: t("common.online"),
    },
  ],
  [ModuleDefinitionTemplates.PRODUCT]: [
    {
      type: InputTypes.TEXT,
      label: t(`common.name`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "name",
      order: 0,
      value: "Sample Product",
    },
    {
      type: InputTypes.TEXTAREA,
      label: t(`common.note`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "note",
      order: 1,
      value: "This is a sample product description.",
    },
    {
      type: InputTypes.NUMBER,
      label: t(`common.price`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "price",
      order: 2,
      value: "99.99",
    },
    {
      type: InputTypes.TEXT,
      label: t(`common.link`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "link",
      order: 3,
      value: "https://www.example.com/product/sample-product",
    },
  ],
  [ModuleDefinitionTemplates.NOTE]: [
    {
      type: InputTypes.TEXTAREA,
      label: t(`common.note`),
      settings: { required: true, maxChar: Lengths.LONG },
      name: "note",
      order: 0,
      value: "This is a sample note.",
    },
  ],
  [ModuleDefinitionTemplates.APPOINTMENT]: [
    {
      type: InputTypes.TEXT,
      label: t(`common.name`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "name",
      order: 0,
      value: "John Doe",
    },
    {
      type: InputTypes.EMAIL,
      label: t(`common.email`),
      settings: { required: true, maxChar: Lengths.EMAIL },
      name: "email",
      order: 1,
      value: "john.doe@example.com",
    },
    {
      type: InputTypes.PHONE,
      label: t(`common.phone`),
      settings: { required: false, maxChar: Lengths.PHONE },
      name: "phone",
      order: 2,
      value: "+1234567890",
    },
    {
      type: InputTypes.DATE,
      label: t(`common.appointmentDate`),
      settings: { required: true, maxChar: Lengths.DESCRIPTION },
      name: "appointmentDate",
      order: 3,
      value: new Date().toLocaleDateString(),
    },
    {
      type: InputTypes.TEXTAREA,
      label: t(`common.note`),
      settings: { required: false, maxChar: Lengths.DESCRIPTION },
      name: "note",
      order: 4,
      value: "Annual health check-up appointment.",
    },
  ],
};

export const getModuleTemplateInputs = (
  template: ModuleDefinitionTemplates,
) => {
  return moduleTemplates[template];
};

export const getPossibleExtraKeysForModuleInstances = (
  moduleDefinition: ModuleDefinition | undefined,
  instances: BaseModuleInstance[],
) => {
  return () => {
    const moduleInputKeys =
      moduleDefinition?.inputs?.map((input) => input.name) || [];
    return (
      uniq(
        instances?.map((inst) => Object.keys(inst?.fields || {})).flat(),
      ).filter((key) => !moduleInputKeys.includes(key)) || []
    );
  };
};
